import { useEffect, useRef, useState } from 'react'

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/
const SCREAMING_SNAKE = /^[A-Z0-9]+(_[A-Z0-9]+)+$/

/** "submittedByLab" / "submitted_by_lab" -> "Submitted by lab". */
function humanizeKey(key) {
  const spaced = String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function titleCase(text) {
  return text
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/** Nested objects become one row each, labelled "Parent · Child". */
function flatten(entries) {
  const rows = []
  entries.forEach(([key, value]) => {
    if (isPlainObject(value)) {
      Object.entries(value).forEach(([childKey, childValue]) => {
        rows.push({
          id: `${key}.${childKey}`,
          label: `${humanizeKey(key)} · ${humanizeKey(childKey)}`,
          value: childValue,
        })
      })
      return
    }
    rows.push({ id: key, label: humanizeKey(key), value })
  })
  return rows
}

function formatDate(text) {
  const date = new Date(text)
  if (Number.isNaN(date.getTime())) return text
  return date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
}

const HEX_VALUE = /^0x[a-fA-F0-9]{16,}$/
const LONG_TEXT_LIMIT = 44

/** "0xd777d61c…4292e": keeps both ends of a hash or address readable. */
function abbreviateHex(text) {
  return `${text.slice(0, 8)}…${text.slice(-6)}`
}

function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text)
  return new Promise((resolve, reject) => {
    const field = document.createElement('textarea')
    field.value = text
    field.setAttribute('readonly', '')
    field.style.position = 'fixed'
    field.style.opacity = '0'
    document.body.appendChild(field)
    field.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(field)
    if (ok) resolve()
    else reject(new Error('Copy failed'))
  })
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef(null)
  useEffect(() => () => clearTimeout(timer.current), [])

  const handleCopy = async () => {
    try {
      await copyToClipboard(text)
      setCopied(true)
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), 1600)
    } catch {
      setCopied(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? 'Copied' : 'Copy value'}
      title={copied ? 'Copied' : 'Copy'}
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-m1-violet ${
        copied
          ? 'border-[#19C670]/50 text-[#19C670]'
          : 'border-m1-line-soft text-m1-muted hover:border-m1-violet/60 hover:text-m1-violet'
      }`}
    >
      <i
        className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-base`}
        aria-hidden="true"
      />
    </button>
  )
}

/** One text value: abbreviated with an ellipsis when it is long, with a copy button beside it. */
function TextValue({ text }) {
  const isHex = HEX_VALUE.test(text)
  const isLong = isHex || text.length > LONG_TEXT_LIMIT
  const shown = isHex ? abbreviateHex(text) : text
  return (
    <span className="flex min-w-0 items-center gap-2 sm:justify-end">
      <span
        title={isLong ? text : undefined}
        className={`min-w-0 text-[17px] text-m1-ink ${isLong ? 'truncate' : 'break-words'} ${isHex ? 'font-m1-mono text-[15px]' : ''}`}
      >
        {shown}
      </span>
      {isLong && <CopyButton text={text} />}
    </span>
  )
}

function ScalarValue({ value }) {
  if (typeof value === 'boolean') {
    return (
      <span className="inline-flex items-center gap-2 text-[17px] text-m1-ink">
        <span
          className={`h-2 w-2 rounded-full ${value ? 'bg-[#19C670] shadow-[0_0_8px_rgba(25,198,112,0.7)]' : 'bg-m1-muted/60'}`}
          aria-hidden="true"
        />
        {value ? 'Yes' : 'No'}
      </span>
    )
  }
  if (value === null || value === undefined || value === '') {
    return <span className="text-[17px] text-m1-muted">—</span>
  }
  const text = String(value)
  if (ISO_DATE.test(text)) {
    return <span className="text-[17px] text-m1-ink">{formatDate(text)}</span>
  }
  if (SCREAMING_SNAKE.test(text)) {
    return <span className="text-[17px] text-m1-ink">{titleCase(text)}</span>
  }
  return <TextValue text={text} />
}

function isCompactItem(item) {
  const text = String(item)
  return !HEX_VALUE.test(text) && text.length <= LONG_TEXT_LIMIT
}

function ValueDisplay({ value }) {
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-[17px] text-m1-muted">—</span>
    // Short items (tags, names) read best inline; long ones (addresses) stack, one per line.
    if (value.every(isCompactItem)) {
      return <span className="text-[17px] text-m1-ink">{value.map(String).join(', ')}</span>
    }
    return (
      <span className="flex min-w-0 flex-col gap-2">
        {value.map((item, index) => (
          <ScalarValue key={`${index}-${String(item)}`} value={item} />
        ))}
      </span>
    )
  }
  return <ScalarValue value={value} />
}

/**
 * Card attributes as a quiet two-column ledger: readable labels, formatted values, hairline
 * separators. Raw keys and JSON blobs never reach the screen.
 */
export default function CardAttributesLedger({ entries }) {
  const rows = flatten(entries)
  if (rows.length === 0) return null
  return (
    <dl className="grid grid-cols-1 gap-x-10 sm:grid-cols-2">
      {rows.map((row) => (
        <div
          key={row.id}
          className="flex min-w-0 flex-col gap-1.5 border-t border-m1-line-soft py-4 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6"
        >
          <dt className="shrink-0 sm:max-w-[45%] font-m1-mono text-[12px] uppercase tracking-[0.1em] text-m1-muted">
            {row.label}
          </dt>
          <dd className="min-w-0 sm:flex-1 sm:text-right">
            <ValueDisplay value={row.value} />
          </dd>
        </div>
      ))}
    </dl>
  )
}
